import React from 'react';
import { motion } from 'framer-motion';
import { 
  Thermometer,
  Container,
  Box,
  AlertTriangle,
  ArrowUpRight,
  ArrowRight,
  CheckCircle2,
  TrainFront
} from 'lucide-react';

const Equipment = () => {
  const equipmentTypes = [
    {
      icon: Box,
      title: 'Dry Van',
      desc: 'Standard 53\' dry vans for general freight. E-track equipped for secure loading.',
      specs: ['53\' x 102"', 'E-track interior', 'Logistics posts'],
      image: 'https://images.unsplash.com/photo-1605745341112-85968b19335b?w=600&auto=format&fit=crop&q=80'
    },
    {
      icon: Thermometer,
      title: 'Refrigerated',
      desc: 'Temperature-controlled trailers for food, pharma, and sensitive cargo.',
      specs: ['-20°F to 70°F', 'Carrier/TK units', 'Real-time temp monitoring'],
      image: 'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=600&auto=format&fit=crop&q=80'
    },
    {
      icon: Container,
      title: 'Flatbed',
      desc: 'Steel, lumber, machinery, and building materials. Open deck versatility.',
      specs: ['48\' & 53\' lengths', 'Max 48,000 lbs', 'Chain/ratchet binders'],
      image: 'https://images.unsplash.com/photo-1616432043562-3671ea2e5242?w=600&auto=format&fit=crop&q=80'
    },
    {
      icon: ArrowUpRight,
      title: 'Step Deck',
      desc: 'Lower deck height for taller loads. Heavy machinery and equipment.',
      specs: ['10\'2" top deck', '38\' lower deck', 'Ramps available'],
      image: 'https://images.unsplash.com/photo-1580674285054-bed31e145f59?w=600&auto=format&fit=crop&q=80'
    },
    {
      icon: AlertTriangle,
      title: 'Hazmat',
      desc: 'Placarded tankers and vans for dangerous goods. TDG and DOT compliant.',
      specs: ['Class 3, 8, 9', 'Placarded units', 'Trained drivers'],
      image: 'https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?w=600&auto=format&fit=crop&q=80'
    },
    {
      icon: TrainFront,
      title: 'Intermodal',
      desc: 'Rail-optimized containers for cost-effective long-haul. Chicago, Memphis, Dallas lanes.',
      specs: ['53\' containers', 'Ramp services', 'Transload available'],
      image: 'https://images.unsplash.com/photo-1591768793355-74d04bb6608f?w=600&auto=format&fit=crop&q=80'
    },
  ];

  const industries = [
    'Consumer Goods',
    'Pharmaceuticals', 
    'Steel & Metals',
    'Lumber & Building',
    'Automotive',
    'Construction',
    'Chemicals',
    'Food & Beverage',
    'Retail',
    'Electronics',
  ];

  const certifications = [
    { code: 'DOT', desc: 'US Department of Transport' },
    { code: 'Transport Canada', desc: 'National Safety Code' },
    { code: 'TDG', desc: 'Transportation of Dangerous Goods' },
    { code: 'Hazmat', desc: 'Certified handlers & drivers' },
    { code: 'CTPAT', desc: 'Customs-Trade Partnership' },
    { code: 'PIP', desc: 'Partners in Protection (CA)' },
  ];

  return (
    <div className="min-h-screen bg-bone">
      {/* HERO */}
      <section className="relative pt-32 pb-20 bg-forest overflow-hidden">
        <div className="absolute inset-0 bg-dots" />
        {/* Animated background orbs */}
        <motion.div 
          className="absolute top-20 right-20 w-64 h-64 bg-amber/20 rounded-full blur-3xl"
          animate={{ 
            scale: [1, 1.2, 1],
            opacity: [0.2, 0.3, 0.2]
          }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div 
          className="absolute bottom-10 left-20 w-80 h-80 bg-white/10 rounded-full blur-3xl"
          animate={{ 
            scale: [1, 1.3, 1],
            x: [0, 30, 0]
          }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
        />
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 relative z-10">
          <div className="max-w-3xl">
            <motion.div 
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="font-mono text-xs tracking-widest uppercase text-amber mb-4"
            >
              Equipment & Commodities
            </motion.div>
            <motion.h1 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="font-display font-black text-display-lg text-white mb-6"
            >
              The Right Equipment for Every Load
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="font-body text-lg text-white/70 max-w-2xl"
            >
              50+ trailers spanning six equipment types. From standard dry van to specialized 
              hazmat and temperature-controlled — we have the assets to move your freight safely.
            </motion.p>
          </div>
        </div>
      </section>

      {/* EQUIPMENT BENTO GRID */}
      <section className="section-padding bg-bone">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {equipmentTypes.map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30, scale: 0.95 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ delay: i * 0.1, duration: 0.5 }}
                whileHover={{ y: -8 }}
                className="group bg-white rounded-2xl overflow-hidden border border-forest/10 hover:border-amber hover:shadow-2xl transition-all duration-300"
              >
                <div className="aspect-[4/3] overflow-hidden relative">
                  <motion.img 
                    src={item.image}
                    alt={item.title}
                    className="w-full h-full object-cover"
                    whileHover={{ scale: 1.1 }}
                    transition={{ duration: 0.6 }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-forest/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </div>
                <div className="p-6">
                  <div className="flex items-center mb-3">
                    <div className="w-10 h-10 bg-forest rounded-lg flex items-center justify-center">
                      <item.icon className="w-5 h-5 text-white" />
                    </div>
                  </div>
                  <h3 className="font-display font-bold text-xl text-forest mb-2 flex items-center gap-2">
                    {item.title}
                    <ArrowUpRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </h3>
                  <p className="font-body text-ink text-sm mb-4">{item.desc}</p>
                  <div className="flex flex-wrap gap-2">
                    {item.specs.map((spec, j) => (
                      <span key={j} className="font-mono text-xs text-forest bg-forest/10 px-2 py-1 rounded">
                        {spec}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* INDUSTRIES MARQUEE */}
      <section className="py-16 bg-forest overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 mb-8">
          <div className="font-mono text-xs tracking-widest uppercase text-amber mb-2">
            Industries Served
          </div>
          <h2 className="font-display font-black text-3xl text-white">
            Commodities We Move
          </h2>
        </div>
        
        <div className="relative">
          <div className="flex animate-marquee">
            {[...industries, ...industries].map((industry, i) => (
              <div 
                key={i}
                className="flex-shrink-0 px-8 py-4 mx-2 bg-forest-2 rounded-full"
              >
                <span className="font-display font-bold text-white whitespace-nowrap">{industry}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COMPLIANCE BAND */}
      <section className="section-padding bg-bone">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <div className="font-mono text-xs tracking-widest uppercase text-forest-2 mb-4">
              Certifications
            </div>
            <h2 className="font-display font-black text-display-lg text-forest">
              Fully Licensed & Compliant
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {certifications.map((cert, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="bg-white p-6 rounded-xl border border-forest/10 flex items-center gap-4"
              >
                <div className="w-12 h-12 bg-forest rounded-lg flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 className="w-6 h-6 text-amber" />
                </div>
                <div>
                  <div className="font-display font-bold text-forest">{cert.code}</div>
                  <div className="font-body text-sm text-ink">{cert.desc}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-white relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-20" />
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 relative z-10 text-center">
          <h2 className="font-display font-black text-display-lg text-forest mb-6">
            Need Specialized Equipment?
          </h2>
          <p className="font-body text-ink/70 text-xl max-w-2xl mx-auto mb-10">
            Tell us about your commodity and we will match you with the right trailer and handling protocols.
          </p>
          <a href="/quote" className="btn-primary text-lg px-10 py-5">
            Request Equipment Quote
            <ArrowRight className="w-6 h-6" />
          </a>
        </div>
      </section>
    </div>
  );
};

export default Equipment;
